import React, { useState, useEffect } from 'react';
import { X, UserPlus, AlertTriangle, FileText, Send } from 'lucide-react';
import { supervisorAlertService } from '../../../services/supervisorAlertService';
import { teamService } from '../../../services/teamService';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const EnforcementPanel = ({ alert, supervisorId, onClose }) => {
  const [actionType, setActionType] = useState('reassign');
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState('');
  const [reason, setReason] = useState('');
  const [disciplinaryType, setDisciplinaryType] = useState('verbal_warning');
  const [pipIssues, setPipIssues] = useState('');
  const [pipGoals, setPipGoals] = useState('');
  const [pipTimeline, setPipTimeline] = useState('30');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    loadTeamMembers();
  }, []);

  const loadTeamMembers = async () => {
    try {
      const members = await teamService?.getTeamMembers();
      setTeamMembers(members);
    } catch (error) {
      console.error('Error loading team members:', error);
    }
  };

  const handleSubmit = async () => {
    if (!reason?.trim()) {
      setMessage({ type: 'error', text: 'Please provide a reason' });
      return;
    }

    try {
      setLoading(true);
      setMessage({ type: '', text: '' });

      if (actionType === 'reassign') {
        if (!selectedMember) {
          setMessage({ type: 'error', text: 'Please select a team member' });
          return;
        }
        await supervisorAlertService?.reassignAlert(
          alert?.id,
          alert?.type,
          selectedMember,
          supervisorId,
          reason
        );
        setMessage({ type: 'success', text: 'Alert reassigned successfully' });
      } else if (actionType === 'escalate') {
        await supervisorAlertService?.escalateToManagement(
          alert?.id,
          alert?.type,
          supervisorId,
          reason
        );
        setMessage({ type: 'success', text: 'Alert escalated to management' });
      } else if (actionType === 'disciplinary') {
        if (!selectedMember) {
          setMessage({ type: 'error', text: 'Please select a team member' });
          return;
        }
        await supervisorAlertService?.initiateDisciplinaryAction(
          selectedMember,
          supervisorId,
          disciplinaryType,
          reason,
          alert?.id
        );
        setMessage({ type: 'success', text: 'Disciplinary action initiated' });
      } else if (actionType === 'pip') {
        if (!selectedMember || !pipIssues?.trim() || !pipGoals?.trim()) {
          setMessage({ type: 'error', text: 'Please fill in all PIP fields' });
          return;
        }
        await supervisorAlertService?.generatePerformanceImprovementPlan(
          selectedMember,
          supervisorId,
          pipIssues,
          pipGoals,
          `${pipTimeline} days`
        );
        setMessage({ type: 'success', text: 'Performance Improvement Plan created' });
      }

      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Error submitting enforcement action:', error);
      setMessage({ type: 'error', text: error?.message || 'Failed to submit action' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Enforcement Actions</h2>
            <p className="text-sm text-gray-600 mt-1">Alert: {alert?.title}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Action Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Action Type</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setActionType('reassign')}
                className={`p-3 rounded-lg border-2 text-left transition-colors ${
                  actionType === 'reassign' ?'border-green-500 bg-green-50' :'border-gray-200 hover:border-gray-300'
                }`}
              >
                <UserPlus className="w-5 h-5 text-green-600 mb-1" />
                <p className="font-medium text-gray-900">Reassign Alert</p>
                <p className="text-xs text-gray-600">Assign to another team member</p>
              </button>

              <button
                onClick={() => setActionType('escalate')}
                className={`p-3 rounded-lg border-2 text-left transition-colors ${
                  actionType === 'escalate' ?'border-orange-500 bg-orange-50' :'border-gray-200 hover:border-gray-300'
                }`}
              >
                <AlertTriangle className="w-5 h-5 text-orange-600 mb-1" />
                <p className="font-medium text-gray-900">Escalate to Management</p>
                <p className="text-xs text-gray-600">Send to higher authority</p>
              </button>

              <button
                onClick={() => setActionType('disciplinary')}
                className={`p-3 rounded-lg border-2 text-left transition-colors ${
                  actionType === 'disciplinary' ?'border-red-500 bg-red-50' :'border-gray-200 hover:border-gray-300'
                }`}
              >
                <FileText className="w-5 h-5 text-red-600 mb-1" />
                <p className="font-medium text-gray-900">Disciplinary Action</p>
                <p className="text-xs text-gray-600">Initiate formal action</p>
              </button>

              <button
                onClick={() => setActionType('pip')}
                className={`p-3 rounded-lg border-2 text-left transition-colors ${
                  actionType === 'pip' ?'border-purple-500 bg-purple-50' :'border-gray-200 hover:border-gray-300'
                }`}
              >
                <FileText className="w-5 h-5 text-purple-600 mb-1" />
                <p className="font-medium text-gray-900">Performance Plan</p>
                <p className="text-xs text-gray-600">Create improvement plan</p>
              </button>
            </div>
          </div>

          {/* Team Member Selection (for reassign, disciplinary, pip) */}
          {(actionType === 'reassign' || actionType === 'disciplinary' || actionType === 'pip') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {actionType === 'reassign' ? 'Reassign To' : 'Team Member'}
              </label>
              <select
                value={selectedMember}
                onChange={(e) => setSelectedMember(e?.target?.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              >
                <option value="">Select team member...</option>
                {teamMembers?.map(member => (
                  <option key={member?.userId} value={member?.userId}>
                    {member?.userProfile?.fullName} - {member?.position}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Disciplinary Type */}
          {actionType === 'disciplinary' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Disciplinary Type</label>
              <select
                value={disciplinaryType}
                onChange={(e) => setDisciplinaryType(e?.target?.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              >
                <option value="verbal_warning">Verbal Warning</option>
                <option value="written_warning">Written Warning</option>
                <option value="final_warning">Final Warning</option>
                <option value="suspension">Suspension</option>
              </select>
            </div>
          )}

          {/* PIP Fields */}
          {actionType === 'pip' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Performance Issues</label>
                <textarea
                  value={pipIssues}
                  onChange={(e) => setPipIssues(e?.target?.value)}
                  placeholder="Describe the performance issues..."
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Improvement Goals</label>
                <textarea
                  value={pipGoals}
                  onChange={(e) => setPipGoals(e?.target?.value)}
                  placeholder="Define specific improvement goals..."
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Timeline (days)</label>
                <Input
                  type="number"
                  value={pipTimeline}
                  onChange={(e) => setPipTimeline(e?.target?.value)}
                  min="7"
                  max="90"
                  className="w-full"
                />
              </div>
            </>
          )}

          {/* Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {actionType === 'pip' ? 'Additional Notes' : 'Reason'} *
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e?.target?.value)}
              placeholder="Provide detailed reason for this action..."
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
            />
          </div>

          {/* Message */}
          {message?.text && (
            <div className={`p-3 rounded-lg ${
              message?.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}>
              {message?.text}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={loading} className="flex items-center gap-2">
            <Send className="w-4 h-4" />
            {loading ? 'Submitting...' : 'Submit Action'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EnforcementPanel;